/**
 * User preferences: local first (localStorage mirror for a fast start, also before login),
 * synchronised with GET/PATCH /api/v2/userdata/preferences once logged in. A missing endpoint
 * (404/501) or an offline server only disables the sync; the local values keep working.
 *
 * Legacy keys are kept (lang, show_url, color_scheme, theme_margin, show_text_search,
 * fixed_table_header, theme_sidebar_pinned); legacy stored booleans as 'true'/'false'.
 */
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { ApiError, api } from '../api/client';

export const COLOR_THEMES = ['default', 'blue', 'green', 'grey', 'orange', 'petrol', 'red', 'sportsman', 'yellow'] as const;
export type ColorTheme = (typeof COLOR_THEMES)[number];
export type ColorScheme = 'auto' | 'light' | 'dark';
export type ShowUrl = 'overlay' | 'new_tab' | 'page';
export type ThemeMargin = 'none' | 'low' | 'medium' | 'high';
/** search row in tables: legacy show_text_search 0 = never, 1 = always, 2 = only while a filter is active */
export type TextSearchMode = 'never' | 'always' | 'filter';
export const TEXT_SEARCH_MODES: readonly TextSearchMode[] = ['never', 'always', 'filter'];
const TEXT_SEARCH_WIRE: Record<TextSearchMode, string> = { never: '0', always: '1', filter: '2' };
export const PAGE_SIZES = [25, 50, 100, 200, 500] as const;

export interface Preferences {
  /** '' = browser language */
  lang: string;
  show_url: ShowUrl;
  color_scheme: ColorScheme;
  color_theme: ColorTheme;
  theme_margin: ThemeMargin;
  show_text_search: TextSearchMode;
  fixed_table_header: boolean;
  theme_sidebar_pinned: boolean;
  /** default page size of table views; null = instance default */
  page_size: number | null;
  /** keyboard remapping: action → bindings */
  shortcuts: Record<string, string[]>;
  [key: string]: unknown;
}

export const DEFAULT_PREFERENCES: Preferences = {
  lang: '',
  show_url: 'overlay',
  color_scheme: 'auto',
  color_theme: 'default',
  theme_margin: 'medium',
  show_text_search: 'always',
  fixed_table_header: true,
  theme_sidebar_pinned: true,
  page_size: null,
  shortcuts: {},
};

export const PREFS_STORAGE_KEY = 'creator.prefs';
const LEGACY_COLOR_MODE_KEY = 'creator.colorMode';

const bool = (v: unknown, d: boolean): boolean => (v === true || v === 'true' || v === 1 || v === '1' ? true : v === false || v === 'false' || v === 0 || v === '0' ? false : d);
function textSearch(v: unknown, d: TextSearchMode): TextSearchMode {
  if (typeof v === 'string' && (TEXT_SEARCH_MODES as readonly string[]).includes(v)) return v as TextSearchMode;
  if (v === '2' || v === 2) return 'filter';
  if (v === true || v === 'true' || v === '1' || v === 1) return 'always';
  if (v === false || v === 'false' || v === '0' || v === 0) return 'never';
  return d;
}
function pageSize(v: unknown): number | null {
  const n = typeof v === 'string' && v.trim() ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 1000 ? n : null;
}
const oneOf = <T extends string>(v: unknown, list: readonly T[], d: T): T => (typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : d);

/** Coerce raw values (server, storage, legacy strings) into typed preferences. */
export function normalizePreferences(raw: Record<string, unknown>): Preferences {
  const d = DEFAULT_PREFERENCES;
  const showUrl = raw.show_url === 'new_window' ? 'new_tab' : raw.show_url;
  return {
    ...raw,
    lang: typeof raw.lang === 'string' ? raw.lang.toLowerCase() : d.lang,
    show_url: oneOf(showUrl, ['overlay', 'new_tab', 'page'] as const, d.show_url),
    color_scheme: oneOf(raw.color_scheme, ['auto', 'light', 'dark'] as const, d.color_scheme),
    color_theme: oneOf(raw.color_theme, COLOR_THEMES, d.color_theme),
    theme_margin: oneOf(raw.theme_margin, ['none', 'low', 'medium', 'high'] as const, d.theme_margin),
    show_text_search: textSearch(raw.show_text_search, d.show_text_search),
    fixed_table_header: bool(raw.fixed_table_header, d.fixed_table_header),
    theme_sidebar_pinned: bool(raw.theme_sidebar_pinned, d.theme_sidebar_pinned),
    page_size: pageSize(raw.page_size),
    shortcuts: raw.shortcuts && typeof raw.shortcuts === 'object' && !Array.isArray(raw.shortcuts) ? (raw.shortcuts as Record<string, string[]>) : {},
  };
}

/** Keys the server accepts (PATCH rejects unknown keys with 400; packages/records PREFERENCE_KEYS). */
export const SERVER_KEYS = [
  'lang',
  'show_url',
  'table_view',
  'mobile_view',
  'default_layout',
  'theme_sidebar_pinned',
  'color_scheme',
  'theme_margin',
  'show_text_search',
  'fixed_table_header',
  'theme',
  'page_size',
  'keyboard_shortcuts',
  'global_search_filter',
] as const;
/** client key → server key where they differ */
export const RENAMED: Record<string, string> = { color_theme: 'theme', shortcuts: 'keyboard_shortcuts' };

/**
 * Server wire format → client values. Server defaults are '' / null ("instance default"); they
 * are dropped so the client defaults apply. `theme` is 1..9 in COLOR_THEMES order;
 * keyboard_shortcuts holds one string per action, several bindings comma separated.
 */
export function fromServer(raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v === null || v === '') continue;
    switch (k) {
      case 'theme':
        if (typeof v === 'number' && COLOR_THEMES[v - 1]) out.color_theme = COLOR_THEMES[v - 1];
        break;
      case 'keyboard_shortcuts':
        if (v && typeof v === 'object')
          out.shortcuts = Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([a, b]) => [a, String(b).split(',').map((x) => x.trim()).filter(Boolean)]));
        break;
      case 'show_text_search':
        out.show_text_search = textSearch(v, DEFAULT_PREFERENCES.show_text_search);
        break;
      default:
        out[k] = v;
    }
  }
  return out;
}

/** Client values → server wire format (only keys the server knows). */
export function toServer(values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    const key = RENAMED[k] ?? k;
    if (!(SERVER_KEYS as readonly string[]).includes(key)) continue;
    if (k === 'color_theme') out.theme = COLOR_THEMES.indexOf(v as ColorTheme) + 1 || null;
    else if (k === 'shortcuts') out.keyboard_shortcuts = Object.fromEntries(Object.entries((v ?? {}) as Record<string, string[]>).map(([a, b]) => [a, b.join(',')]));
    else if (k === 'color_scheme') out.color_scheme = v === 'auto' ? '' : v;
    else if (k === 'show_text_search') out.show_text_search = TEXT_SEARCH_WIRE[textSearch(v, DEFAULT_PREFERENCES.show_text_search)];
    else if (k === 'page_size') out.page_size = pageSize(v);
    else out[key] = v;
  }
  return out;
}

/** GET /userdata/preferences → `{ preferences, defaults, stored }`; only keys the user set win. */
function unwrap(body: unknown): Record<string, unknown> {
  if (!body || typeof body !== 'object') return {};
  const b = body as { preferences?: Record<string, unknown>; stored?: string[] };
  const prefs = b.preferences && typeof b.preferences === 'object' ? b.preferences : (body as Record<string, unknown>);
  const stored = Array.isArray(b.stored) ? new Set(b.stored) : undefined;
  return fromServer(stored ? Object.fromEntries(Object.entries(prefs).filter(([k]) => stored.has(k))) : prefs);
}

function readLocal(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    // colour mode chosen before the preferences existed (public/color-mode.js uses this key)
    const mode = localStorage.getItem(LEGACY_COLOR_MODE_KEY);
    if (parsed.color_scheme === undefined && mode) parsed.color_scheme = mode;
    return parsed;
  } catch {
    return {};
  }
}

export const usePreferencesStore = defineStore('preferences', () => {
  const values = reactive<Preferences>(normalizePreferences(readLocal()));
  /** null = not tried yet, false = endpoint missing / unreachable */
  const serverSync = ref<boolean | null>(null);
  const saving = ref(false);
  let pending: Record<string, unknown> = {};
  let timer: ReturnType<typeof setTimeout> | undefined;
  let flushing: Promise<void> | undefined;
  /** debounce of server writes (ms) */
  const debounceMs = ref(400);

  const localOnly = computed(() => serverSync.value === false);

  function persistLocal(): void {
    try {
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(values));
      // read before first paint by public/color-mode.js
      localStorage.setItem(LEGACY_COLOR_MODE_KEY, values.color_scheme);
      localStorage.setItem('creator.colorTheme', values.color_theme);
    } catch {
      // storage unavailable
    }
  }

  /** Load from the server (after login); server values win over the local mirror. */
  async function sync(): Promise<void> {
    try {
      const body = await api.get<unknown>('/userdata/preferences');
      serverSync.value = true;
      Object.assign(values, normalizePreferences({ ...values, ...unwrap(body) }));
      persistLocal();
    } catch (e) {
      serverSync.value = false;
      if (!(e instanceof ApiError && (e.status === 404 || e.status === 501 || e.status === 405))) console.warn('preferences: sync failed', e);
    }
  }

  function set<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
    setMany({ [key]: value } as Partial<Preferences>);
  }

  function setMany(patch: Partial<Preferences>): void {
    Object.assign(values, normalizePreferences({ ...values, ...patch }));
    persistLocal();
    for (const k of Object.keys(patch)) pending[k] = values[k];
    if (serverSync.value !== true) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void flush().catch(() => {}), debounceMs.value);
  }

  /** Send pending changes now (PATCH, partial). */
  async function flush(): Promise<void> {
    if (timer) clearTimeout(timer);
    timer = undefined;
    if (flushing) await flushing.catch(() => {});
    if (serverSync.value !== true || !Object.keys(pending).length) return;
    const body = pending;
    pending = {};
    const wire = toServer(body);
    if (!Object.keys(wire).length) return;
    saving.value = true;
    flushing = (async () => {
      try {
        await api.patch('/userdata/preferences', wire);
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 501 || e.status === 405)) serverSync.value = false;
        // invalid values (400) are dropped; transient failures are retried with the next change
        else if (!(e instanceof ApiError && e.status === 400)) pending = { ...body, ...pending };
        throw e;
      } finally {
        saving.value = false;
        flushing = undefined;
      }
    })();
    return flushing;
  }

  /**
   * Reset one preference to its default: DELETE /userdata/preferences/:key (server key), then the
   * server's merged answer applies; local-only mode just restores the client default.
   */
  async function resetKey(key: keyof Preferences & string): Promise<void> {
    delete pending[key];
    const def = DEFAULT_PREFERENCES[key];
    Object.assign(values, normalizePreferences({ ...values, [key]: def && typeof def === 'object' ? structuredClone(def) : def }));
    persistLocal();
    const serverKey = RENAMED[key] ?? key;
    if (serverSync.value !== true || !(SERVER_KEYS as readonly string[]).includes(serverKey)) return;
    const body = await api.delete<unknown>(`/userdata/preferences/${encodeURIComponent(serverKey)}`);
    const fromSrv = unwrap(body);
    // keys the user has not stored are left out of unwrap() → the client default stays
    Object.assign(values, normalizePreferences({ ...values, ...(key in fromSrv ? { [key]: fromSrv[key] } : {}) }));
    persistLocal();
  }

  /** Logout: stop syncing, keep the local mirror (language, theme survive on the login page). */
  function reset(): void {
    if (timer) clearTimeout(timer);
    timer = undefined;
    pending = {};
    serverSync.value = null;
  }

  return { values, serverSync, localOnly, saving, debounceMs, sync, set, setMany, flush, resetKey, reset };
});
